import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, MailCheck } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/sonner";
import api, { formatApiError } from "@/lib/api";

export default function ForgotPassword() {
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const [sent, setSent] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!email.trim()) return;
        setLoading(true);
        try {
            await api.post("/auth/forgot-password", { email: email.trim() });
            setSent(true);
        } catch (err) {
            // The endpoint intentionally never fails on unknown email, but
            // network/validation errors can still surface here.
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-[85vh] bg-background py-16">
            <div className="container-page max-w-md">
                <div className="card-soft p-8" data-testid="forgot-password-card">
                    <h1 className="font-heading font-semibold text-2xl text-foreground">Reset your password</h1>
                    <p className="mt-1 text-sm text-muted-foreground">
                        Enter the email on your Simonara account. If it matches an account, we&apos;ll send a one-time link to choose a new password.
                    </p>

                    {sent ? (
                        <div
                            data-testid="forgot-password-sent"
                            className="mt-6 rounded-2xl border border-emerald-500/30 bg-accent p-5 text-sm text-accent-foreground dark:text-emerald-300"
                        >
                            <MailCheck className="mb-2" size={22} />
                            <p className="font-semibold text-foreground dark:text-emerald-100">Check your inbox.</p>
                            <p className="mt-1 text-muted-foreground dark:text-emerald-200/80">
                                If an account exists for <span className="font-medium">{email}</span>, we&apos;ve emailed a password reset link.
                                It expires in 30 minutes.
                            </p>
                            <p className="mt-3 text-xs text-muted-foreground dark:text-emerald-200/70">
                                Didn&apos;t get it? Check spam, or wait a minute and{" "}
                                <button
                                    type="button"
                                    onClick={() => { setSent(false); }}
                                    className="underline font-medium hover:text-emerald-600"
                                    data-testid="forgot-password-retry"
                                >
                                    try again
                                </button>.
                            </p>
                        </div>
                    ) : (
                        <form onSubmit={submit} className="mt-6 space-y-4">
                            <div>
                                <Label htmlFor="forgot-email">Email</Label>
                                <Input
                                    id="forgot-email"
                                    name="email"
                                    type="email"
                                    required
                                    autoComplete="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    data-testid="forgot-email-input"
                                    className="mt-2"
                                />
                            </div>
                            <button
                                type="submit"
                                disabled={loading}
                                data-testid="forgot-submit-btn"
                                className="btn-green w-full disabled:opacity-60"
                            >
                                {loading ? (
                                    <>
                                        <Loader2 size={18} className="mr-2 animate-spin" /> Sending link…
                                    </>
                                ) : (
                                    "Send reset link"
                                )}
                            </button>
                        </form>
                    )}

                    <p className="mt-6 text-center text-sm text-muted-foreground">
                        Remembered it?{" "}
                        <Link to="/login" data-testid="forgot-back-to-login" className="font-semibold text-brand-green dark:text-emerald-400 hover:underline">
                            Back to log in
                        </Link>
                    </p>
                </div>
            </div>
        </div>
    );
}
